// GrapesJS LayoutEnhancer: Precision Spacing Tool
// A revolutionary plugin for precise layout control and spacing adjustments

export default function layoutEnhancer(editor, opts = {}) {
  const options = {
    // Filter of components which will have this functionality
    componentFilter: opts.componentFilter || ['*'], // '*' means all components
    // Default spacing values
    defaultMargin: opts.defaultMargin || 0,
    defaultPadding: opts.defaultPadding || 0,
    // UI options
    showInPanel: opts.showInPanel !== false,
    panelTitle: opts.panelTitle || 'Layout Enhancer',
    // Spacing presets
    spacingPresets: opts.spacingPresets || {
      'Tight': { margin: '4px', padding: '8px' },
      'Comfortable': { margin: '8px', padding: '16px' },
      'Spacious': { margin: '16px', padding: '24px' },
      'Loose': { margin: '24px', padding: '32px' }
    }
  };

  let currentComponent = null;
  let spacingPanel = null;

  // Check if component should have layout enhancer functionality
  function shouldEnhanceComponent(component) {
    if (!component) return false;
    
    const tagName = component.get('tagName');
    const type = component.get('type');
    
    // Check against component filter
    return options.componentFilter.some(filter => {
      if (filter === '*') return true;
      if (filter === tagName) return true;
      if (filter === type) return true;
      if (typeof filter === 'function') return filter(component);
      return false;
    });
  }

  // Create the spacing control panel
  function createSpacingPanel() {
    const panel = document.createElement('div');
    panel.className = 'layout-enhancer-panel';
    panel.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      width: 320px;
      background: #ffffff;
      border: 2px solid #2563eb;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      z-index: 99999;
      padding: 20px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      display: none;
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
    `;

    const title = document.createElement('h3');
    title.textContent = options.panelTitle;
    title.style.cssText = `
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #1f2937;
    `;

    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #6b7280;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: all 0.2s ease;
    `;
    closeBtn.addEventListener('mouseenter', () => {
      closeBtn.style.background = '#f3f4f6';
      closeBtn.style.color = '#374151';
    });
    closeBtn.addEventListener('mouseleave', () => {
      closeBtn.style.background = 'none';
      closeBtn.style.color = '#6b7280';
    });
    closeBtn.addEventListener('click', hideSpacingPanel);

    header.appendChild(title);
    header.appendChild(closeBtn);

    // Component info
    const componentInfo = document.createElement('div');
    componentInfo.className = 'component-info';
    componentInfo.style.cssText = `
      margin-bottom: 16px;
      padding: 12px;
      background: #f9fafb;
      border-radius: 8px;
      border-left: 4px solid #2563eb;
    `;

    const componentName = document.createElement('div');
    componentName.className = 'component-name';
    componentName.style.cssText = `
      font-weight: 600;
      color: #374151;
      margin-bottom: 4px;
    `;

    const componentType = document.createElement('div');
    componentType.className = 'component-type';
    componentType.style.cssText = `
      font-size: 12px;
      color: #6b7280;
    `;

    componentInfo.appendChild(componentName);
    componentInfo.appendChild(componentType);

    // Presets section
    const presetsSection = document.createElement('div');
    presetsSection.style.cssText = `
      margin-bottom: 20px;
    `;

    const presetsLabel = document.createElement('label');
    presetsLabel.textContent = 'Quick Presets';
    presetsLabel.style.cssText = `
      display: block;
      font-weight: 500;
      color: #374151;
      margin-bottom: 8px;
    `;

    const presetsContainer = document.createElement('div');
    presetsContainer.style.cssText = `
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    `;

    Object.entries(options.spacingPresets).forEach(([name, values]) => {
      const presetBtn = document.createElement('button');
      presetBtn.textContent = name;
      presetBtn.style.cssText = `
        padding: 8px 12px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        background: #ffffff;
        color: #374151;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
      `;
      presetBtn.addEventListener('mouseenter', () => {
        presetBtn.style.background = '#f3f4f6';
        presetBtn.style.borderColor = '#9ca3af';
      });
      presetBtn.addEventListener('mouseleave', () => {
        presetBtn.style.background = '#ffffff';
        presetBtn.style.borderColor = '#d1d5db';
      });
      presetBtn.addEventListener('click', () => applyPreset(values));
      presetsContainer.appendChild(presetBtn);
    });

    presetsSection.appendChild(presetsLabel);
    presetsSection.appendChild(presetsContainer);

    // Margin controls
    const marginSection = createSpacingSection('Margin', 'margin');
    const paddingSection = createSpacingSection('Padding', 'padding');

    // Live preview toggle
    const previewSection = document.createElement('div');
    previewSection.style.cssText = `
      margin-top: 20px;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;
    `;

    const previewLabel = document.createElement('label');
    previewLabel.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
      color: #374151;
      cursor: pointer;
    `;

    const previewCheckbox = document.createElement('input');
    previewCheckbox.type = 'checkbox';
    previewCheckbox.checked = true;
    previewCheckbox.style.cssText = `
      width: 16px;
      height: 16px;
    `;

    const previewText = document.createElement('span');
    previewText.textContent = 'Live Preview';

    previewLabel.appendChild(previewCheckbox);
    previewLabel.appendChild(previewText);

    previewSection.appendChild(previewLabel);

    // Assemble panel
    panel.appendChild(header);
    panel.appendChild(componentInfo);
    panel.appendChild(presetsSection);
    panel.appendChild(marginSection);
    panel.appendChild(paddingSection);
    panel.appendChild(previewSection);

    return panel;
  }

  // Create spacing section (margin or padding)
  function createSpacingSection(title, property) {
    const section = document.createElement('div');
    section.style.cssText = `
      margin-bottom: 20px;
    `;

    const sectionTitle = document.createElement('h4');
    sectionTitle.textContent = title;
    sectionTitle.style.cssText = `
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      color: #374151;
    `;

    const controls = document.createElement('div');
    controls.style.cssText = `
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    `;

    // Individual side controls
    const sides = [
      { name: 'Top', key: 'top' },
      { name: 'Right', key: 'right' },
      { name: 'Bottom', key: 'bottom' },
      { name: 'Left', key: 'left' }
    ];

    sides.forEach(side => {
      const control = createSpacingControl(side.name, property, side.key);
      controls.appendChild(control);
    });

    // All sides control
    const allSidesControl = createSpacingControl('All Sides', property, 'all');
    allSidesControl.style.gridColumn = '1 / -1';
    controls.appendChild(allSidesControl);

    section.appendChild(sectionTitle);
    section.appendChild(controls);

    return section;
  }

  // Create individual spacing control
  function createSpacingControl(label, property, side) {
    const control = document.createElement('div');
    control.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 4px;
    `;

    const labelEl = document.createElement('label');
    labelEl.textContent = label;
    labelEl.style.cssText = `
      font-size: 12px;
      font-weight: 500;
      color: #6b7280;
    `;

    const input = document.createElement('input');
    input.type = 'number';
    input.min = '0';
    input.step = '1';
    input.placeholder = '0';
    input.style.cssText = `
      padding: 6px 8px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      font-size: 12px;
      transition: border-color 0.2s ease;
    `;

    input.addEventListener('focus', () => {
      input.style.borderColor = '#2563eb';
    });

    input.addEventListener('blur', () => {
      input.style.borderColor = '#d1d5db';
    });

    input.addEventListener('input', (e) => {
      const value = e.target.value;
      if (value !== '') {
        updateSpacing(property, side, `${value}px`);
      }
    });

    // Store reference for later updates
    control.dataset.property = property;
    control.dataset.side = side;
    control.input = input;

    control.appendChild(labelEl);
    control.appendChild(input);

    return control;
  }

  // Update spacing on component
  function updateSpacing(property, side, value) {
    if (!currentComponent) return;

    const style = currentComponent.getStyle();
    const currentValue = style[property] || '';

    let newValue;
    if (side === 'all') {
      newValue = value;
    } else {
      // Parse current value and update specific side
      const sides = ['top', 'right', 'bottom', 'left'];
      const values = currentValue.split(' ').map(v => v.trim());
      
      // Ensure we have 4 values
      while (values.length < 4) {
        values.push('0px');
      }

      const sideIndex = sides.indexOf(side);
      if (sideIndex !== -1) {
        values[sideIndex] = value;
      }

      newValue = values.join(' ');
    }

    currentComponent.setStyle({ [property]: newValue });
    editor.refresh();
  }

  // All Tailwind spacing classes that presets might add — cleared before each preset applies
  const TAILWIND_SPACING_CLASSES = [
    'm-0','m-1','m-2','m-3','m-4','m-5','m-6','m-8','m-10','m-12',
    'p-0','p-1','p-2','p-3','p-4','p-5','p-6','p-8','p-10','p-12',
  ];

  // Apply preset values
  function applyPreset(preset) {
    if (!currentComponent) return;

    if (preset.classes) {
      // Tailwind class presets — remove any previously applied spacing classes first
      TAILWIND_SPACING_CLASSES.forEach(cls => currentComponent.removeClass(cls));
      preset.classes.forEach(cls => currentComponent.addClass(cls));
      // Also clear any conflicting inline margin/padding so Tailwind wins
      const style = currentComponent.getStyle();
      delete style.margin;
      delete style.padding;
      currentComponent.setStyle(style);
    } else {
      Object.entries(preset).forEach(([property, value]) => {
        currentComponent.setStyle({ [property]: value });
      });
    }

    editor.refresh();
    updatePanelValues();
  }

  // Update panel input values
  function updatePanelValues() {
    if (!currentComponent || !spacingPanel) return;

    const style = currentComponent.getStyle();
    
    // Update margin inputs
    updateControlValues('margin', style.margin || '0px');
    
    // Update padding inputs
    updateControlValues('padding', style.padding || '0px');
  }

  // Update control values
  function updateControlValues(property, value) {
    const controls = spacingPanel.querySelectorAll(`[data-property="${property}"]`);
    
    controls.forEach(control => {
      const input = control.input;
      const side = control.dataset.side;
      
      if (side === 'all') {
        input.value = parseFloat(value) || 0;
      } else {
        const values = value.split(' ').map(v => parseFloat(v) || 0);
        const sideIndex = ['top', 'right', 'bottom', 'left'].indexOf(side);
        input.value = values[sideIndex] || 0;
      }
    });
  }

  // Show spacing panel
  function showSpacingPanel(component) {
    if (!shouldEnhanceComponent(component)) return;

    currentComponent = component;
    
    if (!spacingPanel) {
      spacingPanel = createSpacingPanel();
      document.body.appendChild(spacingPanel);
    }

    // Update component info
    const componentName = spacingPanel.querySelector('.component-name');
    const componentType = spacingPanel.querySelector('.component-type');
    
    componentName.textContent = component.get('tagName') || 'Component';
    componentType.textContent = component.get('type') || 'Unknown type';

    // Update values
    updatePanelValues();

    // Show panel
    spacingPanel.style.display = 'block';
  }

  // Hide spacing panel
  function hideSpacingPanel() {
    if (spacingPanel) {
      spacingPanel.style.display = 'none';
    }
    currentComponent = null;
  }

  // Add panel button to editor
  if (options.showInPanel) {
    // Try different panel names that might exist in GrapesJS
    const panelNames = ['views-container', 'options', 'panel-manager', 'panel-options'];
    
    for (const panelName of panelNames) {
      const panel = editor.Panels.getPanel(panelName);
      if (panel) {
        try {
          panel.get('buttons').add([
            {
              id: 'layout-enhancer',
              className: 'btn-toggle',
              command: 'layout-enhancer-toggle',
              attributes: {
                title: 'Layout Enhancer (Ctrl/Cmd + L)'
              }
            }
          ]);
          console.log(`✅ LayoutEnhancer button added to panel: ${panelName}`);
          break; // Successfully added, exit loop
        } catch (error) {
          console.log(`⚠️ Could not add button to panel: ${panelName}`, error);
        }
      }
    }
    
    // Alternative: Add to the main toolbar if panels don't work
    try {
      const mainPanel = editor.Panels.getPanel('views');
      if (mainPanel) {
        mainPanel.get('buttons').add([
          {
            id: 'layout-enhancer',
            className: 'btn-toggle',
            command: 'layout-enhancer-toggle',
            attributes: {
              title: 'Layout Enhancer (Ctrl/Cmd + L)'
            }
          }
        ]);
        console.log('✅ LayoutEnhancer button added to main views panel');
      }
    } catch (error) {
      console.log('⚠️ Could not add button to main views panel:', error);
    }
  }

  // Register commands
  editor.Commands.add('layout-enhancer-toggle', {
    run: (editor) => {
      const selected = editor.getSelected();
      if (selected) {
        showSpacingPanel(selected);
      }
    },
    stop: () => {
      hideSpacingPanel();
    }
  });

  // Event listeners
  editor.on('component:selected', (component) => {
    if (shouldEnhanceComponent(component)) {
      // Add visual indicator
      const el = component.getEl();
      if (el) {
        el.style.outline = '2px solid #2563eb';
        el.style.outlineOffset = '2px';
      }
    }
  });

  editor.on('component:deselected', (component) => {
    // Remove visual indicator
    const el = component.getEl();
    if (el) {
      el.style.outline = '';
      el.style.outlineOffset = '';
    }
    
    // Hide panel if component is deselected
    if (currentComponent === component) {
      hideSpacingPanel();
    }
  });

  // Global keyboard shortcut (Ctrl/Cmd + L)
  document.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'l') {
      e.preventDefault();
      const selected = editor.getSelected();
      if (selected && shouldEnhanceComponent(selected)) {
        showSpacingPanel(selected);
      } else {
        console.log('⚠️ No compatible component selected for LayoutEnhancer');
      }
    }
  });

  // Add CSS for better styling
  const style = document.createElement('style');
  style.textContent = `
    .layout-enhancer-panel input:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
    }
    
    .layout-enhancer-panel button:hover {
      transform: translateY(-1px);
    }
    
    .layout-enhancer-panel button:active {
      transform: translateY(0);
    }
  `;
  document.head.appendChild(style);

  // Expose API
  editor.LayoutEnhancer = {
    show: showSpacingPanel,
    hide: hideSpacingPanel,
    shouldEnhance: shouldEnhanceComponent,
    updateSpacing,
    applyPreset
  };

  // Manual button creation function for testing
  window.createLayoutEnhancerButton = () => {
    console.log('🔧 Creating LayoutEnhancer button manually...');
    
    // List all available panels
    const panels = editor.Panels.getAll();
    console.log('📋 Available panels:', panels.map(p => p.getId()));
    
    // Try to add button to each panel
    panels.forEach(panel => {
      try {
        const panelId = panel.getId();
        panel.get('buttons').add([
          {
            id: 'layout-enhancer',
            className: 'btn-toggle',
            command: 'layout-enhancer-toggle',
            attributes: {
              title: 'Layout Enhancer (Ctrl/Cmd + L)'
            }
          }
        ]);
        console.log(`✅ Button added to panel: ${panelId}`);
      } catch (error) {
        console.log(`❌ Failed to add button to panel: ${panel.getId()}`, error);
      }
    });
  };

  // Debug function to test keyboard shortcut
  window.testLayoutEnhancer = () => {
    console.log('🧪 Testing LayoutEnhancer...');
    const selected = editor.getSelected();
    console.log('Selected component:', selected);
    if (selected) {
      console.log('Component type:', selected.get('type'));
      console.log('Component tagName:', selected.get('tagName'));
      console.log('Should enhance:', shouldEnhanceComponent(selected));
      if (shouldEnhanceComponent(selected)) {
        showSpacingPanel(selected);
      }
    } else {
      console.log('No component selected');
    }
  };

  console.log('🎨 LayoutEnhancer plugin loaded successfully!');
  console.log('📋 Usage: Select a component and press Ctrl/Cmd + L to open the spacing panel');
  console.log('🔧 If button is missing, run: createLayoutEnhancerButton() in console');
} 